import { GraphForm } from "./graph_form";
import { TargetDate } from "./target_date";
import { Keyword } from "./keyword";
import $ from "jquery";
import { Scattergram, ScattergramPoint } from "./scattergram";

// Interfaces
interface Location {
  x: number;
  y: number;
}

interface ScattergramOptions {
  width?: number;
  height?: number;
}

class ScattergramForm extends GraphForm {
  private static readonly PARTITION_NAMES: Record<string, string> = {
    yearly: "年",
    monthly: "月",
    weekly: "週",
    daily: "日",
  };

  private static readonly MAGNIFICATION_DIFF = 0.6;
  private static readonly MAGNIFICATION_MAX = 3.0;
  private static readonly MAGNIFICATION_MIN = 1.0;

  protected dataLabels: any;
  protected keyword: Keyword;
  protected targetDate: TargetDate;
  protected graph: Scattergram | null;
  protected options: ScattergramOptions;

  constructor(dataLabels: any, keyword: Keyword, targetDate: TargetDate) {
    super();
    this.dataLabels = dataLabels;
    this.keyword = keyword;
    this.targetDate = targetDate;
    this.graph = null;
    this.options = {};
  }

  protected formElement(): JQuery {
    return $("#dashboards_form");
  }

  protected partitionElement(): JQuery {
    return $("#partition");
  }

  protected locationElement(): JQuery {
    return $("#location");
  }

  protected magnificationElement(): JQuery {
    return $("#magnification");
  }

  protected canvasElement(): JQuery {
    return $("#scattergram");
  }

  protected dimensionElement(): JQuery {
    return $("#dimension");
  }

  protected areaElement(): JQuery {
    return $("#area");
  }

  protected categoryElement(): JQuery {
    return $("#category");
  }

  getPartition(): string {
    return this.partitionElement().val() as string;
  }

  getDimension(): string {
    return this.dimensionElement().val() as string;
  }

  getArea(): string {
    return this.areaElement().val() as string;
  }

  getCategory(): string {
    return this.categoryElement().val() as string;
  }

  supportedPartitions(): string[] {
    return this.canvasElement().data("supportedPartitions");
  }

  initialize(options: ScattergramOptions): void {
    this.options = options;
    this.initializeDate();
    this.initializeScattergram();
  }

  private initializeDate(): void {
    this.targetDate.initialize();
  }

  private initializeScattergram(): void {
    $(".zoom_in").click((_e) => {
      this.increaseMagnification();
      return true;
    });

    $(".zoom_out").click((_e) => {
      this.decreaseMagnification();
      return true;
    });

    this.formElement().submit((_e) => {
      if (this.graph) {
        this.setLocation(this.graph.location());
      }
      return true;
    });
  }

  render(): void {
    // Create new Scattergram instance
    this.graph = new Scattergram(
      this.canvasElement(),
      this.getMagnification(),
      this.getLocation(),
      this.options,
    );

    // Check if partition is supported
    if (!this.supportedPartitions().includes(this.getPartition())) {
      const partitionName =
        ScattergramForm.PARTITION_NAMES[this.getPartition()];
      if (this.graph) {
        this.graph.drawMessage(`
          集計単位が「${partitionName}」のとき、このグラフは表示できません。
        `);
      }
      return;
    }

    // Check if keyword is found
    if (this.keyword && (this.keyword as any).isNotFound()) {
      if (this.graph) {
        this.graph.drawMessage(`
          「${this.keyword.value()}」は見付かりませんでした。
          別の単語をお試しください。
        `);
      }
      return;
    }

    // Fetch and display points
    this.fetchItemPoints(this.getAjaxUrl(), this.getQuery())
      .then((items: ScattergramPoint[]) => {
        if ($.isEmptyObject(items)) {
          this.drawNoItemsMessage();
        } else {
          if (this.graph) {
            this.graph.drawPoints(items);
          }
        }
      })
      .catch((errorResponse: any) => {
        if (this.graph) {
          this.graph.drawErrorMessage(`
            申し訳ありません、通信エラーが発生しました。
            少し時間をおいて再度お試しください。
            ステータス: ${errorResponse.status}, ${new Date()}
          `);
        }
      })
      .finally(() => {
        this.enableCsvButton($(".scattergram_box .csv_button"));
      });
  }

  private fetchItemPoints(
    ajaxUrl: string,
    params: Record<string, any>,
  ): Promise<ScattergramPoint[]> {
    const labels = this.dataLabels;
    const pointUrl = this.getPointUrl();

    return new Promise((resolve, reject) => {
      $.ajax({
        url: ajaxUrl,
        data: params,
        dataType: "json",
      })
        .done((res: any) => {
          const points = res.values.map(
            ([id, name, x, y]: [string, string, number, number]) => {
              return this.fixPoint(
                new ScattergramPoint(id, name, x, y, labels, pointUrl),
              );
            },
          );
          resolve(points);
        })
        .fail((errorResponse: any) => {
          reject(errorResponse);
        });
    });
  }

  // Override point for customization
  protected fixPoint(point: ScattergramPoint): ScattergramPoint {
    return point;
  }

  // Override for custom no items message
  protected drawNoItemsMessage(): void {
    if (this.graph) {
      this.graph.drawMessage(`
        「${this.keyword.value()}」は見つかりませんでした。
        別の単語をお試しください。
      `);
    }
  }

  private getAjaxUrl(): string {
    return this.canvasElement().data("ajaxUrl");
  }

  private getPointUrl(): string {
    return this.canvasElement().data("pointUrl");
  }

  getQuery(): Record<string, any> {
    return {
      partition: this.getPartition(),
      target: this.targetDate.stringValue(),
      magnification: this.getMagnification(),
      dimension: this.getDimension(),
      area: this.getArea(),
      category: this.getCategory(),
    };
  }

  private getLocation(): Location | null {
    return this.parseLocation(this.locationElement().val() as string);
  }

  private parseLocation(loc: string): Location | null {
    if (!loc || loc === "") {
      return null;
    }
    const [x, y] = loc.split(",", 2).map(parseFloat);
    return { x, y };
  }

  private setLocation(pos: Location): void {
    this.locationElement().val(`${pos.x.toFixed(3)},${pos.y.toFixed(3)}`);
  }

  private getMagnification(): number {
    return parseFloat(this.magnificationElement().val() as string);
  }

  private increaseMagnification(): void {
    this.magnificationElement().val((index: number, value: string) => {
      return Math.min(
        parseFloat(value) + ScattergramForm.MAGNIFICATION_DIFF,
        ScattergramForm.MAGNIFICATION_MAX,
      ).toFixed(1);
    });
  }

  private decreaseMagnification(): void {
    this.magnificationElement().val((index: number, value: string) => {
      return Math.max(
        parseFloat(value) - ScattergramForm.MAGNIFICATION_DIFF,
        ScattergramForm.MAGNIFICATION_MIN,
      ).toFixed(1);
    });
  }
}

export { ScattergramForm };
